import { Scan, SpinnerGap, Prohibit, Clock, WarningCircle, CheckCircle, Gear, XCircle } from "@phosphor-icons/react";

// The "Advanced / AI Video Analysis" status card — extracted verbatim (no
// behavior change). Purely a display of `analysis` + a couple of actions.
export function VideoAnalysisCard({
  analysis, canManageGame, analyzing, cancelling, now,
  stageLabel, elapsedLabel, formatMinutes, onRunAnalysis, onCancelAnalysis, onOpenSettings,
}) {
  return (
    <div className="bg-[#121212] border border-zinc-800 p-6 rounded-sm lg:col-span-2" data-testid="analysis-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl text-white flex items-center gap-2 mb-1"><Scan size={22} weight="bold" className="text-[#CCFF00]" /> Video analysis</h2>
          <p className="text-xs text-zinc-500 max-w-xl">Detects and tracks players and the ball across the footage (experimental). Results are anonymous — no player identity or stats yet. Runs in the background; you can leave this page.</p>
        </div>
        {canManageGame && (!analysis || ["none", "failed", "complete", "cancelled"].includes(analysis.status)) && (
          <button type="button" onClick={onRunAnalysis} disabled={analyzing} data-testid="analyze-video-btn" className="shrink-0 flex items-center gap-2 bg-[#CCFF00] text-black font-label text-xs px-5 py-3 rounded-sm hover:bg-[#B3E600] transition-colors duration-150 disabled:opacity-40">
            <Scan size={16} weight="bold" /> {analysis && ["complete", "failed", "cancelled"].includes(analysis.status) ? "Re-analyze" : "Analyze video"}
          </button>
        )}
      </div>

      {analysis && analysis.status !== "none" && (
        <div className="mt-5 border-t border-zinc-800 pt-5" data-testid="analysis-status">
          {analysis.status === "queued" && (
            <div>
              <p className="flex items-center gap-2 text-sm text-zinc-400"><SpinnerGap size={18} className="animate-spin text-[#CCFF00]" /> Queued — starting up…</p>
              {canManageGame && (
                <button type="button" onClick={onCancelAnalysis} disabled={cancelling} data-testid="cancel-analysis-btn" className="mt-3 flex items-center gap-2 text-zinc-500 hover:text-[#FF3B30] transition-colors duration-150 font-label text-xs disabled:opacity-40">
                  <Prohibit size={14} weight="bold" /> {cancelling ? "Cancelling…" : "Cancel"}
                </button>
              )}
            </div>
          )}
          {analysis.status === "processing" && (
            <div>
              <p className="flex items-center gap-2 text-sm text-zinc-300 mb-3" data-testid="analysis-processing-label"><SpinnerGap size={18} className="animate-spin text-[#CCFF00]" /> {stageLabel(analysis)}</p>
              <div className="w-full h-2 bg-[#0A0A0A] border border-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-[#CCFF00] transition-all duration-500" style={{ width: `${Math.round((analysis.progress || 0) * 100)}%` }} data-testid="analysis-progress-bar" />
              </div>
              <div className="flex items-center gap-4 mt-2 flex-wrap">
                <p className="text-xs text-zinc-500" data-testid="analysis-progress-pct">{Math.round((analysis.progress || 0) * 100)}%</p>
                {analysis.started_at && (
                  <p className="text-xs text-zinc-500 flex items-center gap-1" data-testid="analysis-running-elapsed"><Clock size={12} /> Running {elapsedLabel(analysis.started_at, now)}</p>
                )}
                {analysis.estimated_remaining_seconds != null && analysis.estimated_remaining_seconds > 0 && (
                  <p className="text-xs text-zinc-500 flex items-center gap-1" data-testid="analysis-eta"><Clock size={12} /> ~{formatMinutes(analysis.estimated_remaining_seconds)} remaining</p>
                )}
                {analysis.last_progress_at && (() => {
                  const staleSecs = Math.max(0, Math.floor((now - new Date(analysis.last_progress_at).getTime()) / 1000));
                  const stale = staleSecs > 180;
                  return (
                    <p className={`text-xs flex items-center gap-1 ${stale ? "text-amber-400" : "text-zinc-600"}`} data-testid="analysis-last-update">
                      {stale && <WarningCircle size={12} weight="fill" />} Updated {elapsedLabel(analysis.last_progress_at, now)} ago{stale ? " — this can happen on very large files" : ""}
                    </p>
                  );
                })()}
              </div>
              {canManageGame && (
                <button type="button" onClick={onCancelAnalysis} disabled={cancelling} data-testid="cancel-analysis-btn" className="mt-3 flex items-center gap-2 text-zinc-500 hover:text-[#FF3B30] transition-colors duration-150 font-label text-xs disabled:opacity-40">
                  <Prohibit size={14} weight="bold" /> {cancelling ? "Cancelling…" : "Cancel"}
                </button>
              )}
            </div>
          )}
          {analysis.status === "complete" && (
            <div className="space-y-2" data-testid="analysis-complete">
              <p className="flex items-center gap-2 text-sm text-[#32D74B]"><CheckCircle size={18} weight="fill" /> Analysis complete</p>
              <p className="text-sm text-zinc-300">
                <span className="font-heading text-2xl text-white">{analysis.player_track_count || 0}</span> player track{analysis.player_track_count === 1 ? "" : "s"} detected
                {" · "}
                {analysis.ball_detection_count > 0
                  ? <>partial ball tracking (<span className="text-white">{analysis.ball_detection_count}</span> detections)</>
                  : "no ball detections"}
              </p>
              <p className="text-xs text-zinc-600">Sampled ~1 frame/sec · confidence scores stored per detection. See "Player identification" below to confirm who's who.</p>
            </div>
          )}
          {analysis.status === "failed" && (
            <div className="space-y-2" data-testid="analysis-failed">
              <p className="flex items-center gap-2 text-sm text-[#FF3B30]"><WarningCircle size={18} weight="fill" /> Analysis failed</p>
              <p className="text-xs text-zinc-500">{analysis.error || "Something went wrong while processing the video."}</p>
              {/authentication/i.test(analysis.error || "") && (
                <button type="button" onClick={onOpenSettings} data-testid="go-to-settings-btn" className="flex items-center gap-2 font-label text-xs bg-[#CCFF00] text-black px-4 py-2 rounded-sm hover:bg-[#B3E600] transition-colors duration-150">
                  <Gear size={14} weight="bold" /> Open Settings
                </button>
              )}
            </div>
          )}
          {analysis.status === "cancelled" && (
            <div className="space-y-2" data-testid="analysis-cancelled">
              <p className="flex items-center gap-2 text-sm text-zinc-400"><XCircle size={18} weight="fill" /> Analysis cancelled</p>
              <p className="text-xs text-zinc-500">You stopped this analysis before it finished. Click Re-analyze to start again.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
